import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { Task } from '../../views/tasks/TaskTypes';
import { Ticket, TicketMessage } from './TicketTypes';

// Loads a ticket with its conversation and tasks and refreshes them after a change
export const useTicketConversation = (id: string | number | null | undefined) => {
    const { handleLogout } = useAuthContext();
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [messages, setMessages] = useState<TicketMessage[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loadStatus, setLoadStatus] = useState<'idle' | 'loading' | 'loaded'>('idle');

    // Keep the logout handler in a ref, its identity changes on every render and would refire the fetch effects
    const handleLogoutRef = useRef(handleLogout);
    handleLogoutRef.current = handleLogout;

    // Fetch the ticket details
    const fetchTicket = useCallback(async () => {
        if (!id) {
            setTicket(null);
            return;
        }
        try {
            const data = await fetchData({ apiUrl: `get_ticket/${id}`, handleLogout: handleLogoutRef.current });
            if (data) setTicket(data);
        } catch (error) {
            console.error('Failed to load ticket', error);
        }
    }, [id]);

    // Fetch the conversation of the ticket
    const fetchMessages = useCallback(async () => {
        if (!id) {
            setMessages([]);
            return;
        }
        try {
            const data = await fetchData({ apiUrl: `get_ticket_message_list/${id}`, handleLogout: handleLogoutRef.current });
            if (Array.isArray(data)) setMessages(data);
        } catch (error) {
            console.error('Failed to load ticket messages', error);
        }
    }, [id]);

    // Fetch the tasks of the ticket
    const fetchTasks = useCallback(async () => {
        if (!id) {
            setTasks([]);
            return;
        }
        try {
            const data = await fetchData({ apiUrl: 'get_task_list', params: { ticket: id, deleted: false, page_size: 50 }, handleLogout: handleLogoutRef.current });
            if (data?.results) setTasks(data.results);
        } catch (error) {
            console.error('Failed to load ticket tasks', error);
        }
    }, [id]);

    // Load everything when the ticket changes
    useEffect(() => {
        setLoadStatus('loading');
        Promise.all([fetchTicket(), fetchMessages(), fetchTasks()]).then(() => setLoadStatus('loaded'));
    }, [fetchTicket, fetchMessages, fetchTasks]);

    // Refresh the ticket and the conversation after a change
    const refresh = useCallback(() => {
        fetchTicket();
        fetchMessages();
        fetchTasks();
    }, [fetchTicket, fetchMessages, fetchTasks]);

    return { ticket, messages, tasks, loadStatus, refresh };
};
